import { useCallback, useMemo } from 'react';
import { captureHeapEvent, HEAP_EVENT_NAMES } from '@blocs.heap';
import { useTranslation } from '@blocs.i18n';
import { PaywallLocation, PurchaseResultType, useBillingPopup } from '@blocs.paywall';
import { useRouter } from '@blocs.router';
import routeHelper from '../../routes/routeHelper';
import { useArtistContext } from '../core/ArtistContext';

export type UseHeadshotMonthlyModalParams = {
  /** Closes the open headshots modal; owned by `useHeadshotModal`. */
  onClose: () => void;
};

/**
 * Monthly "Unlock One More Session" upsell shown once the yearly session is used:
 * the next booking date and the upgrade callback.
 * `useHeadshotModal` owns which modal is open.
 */
export const useHeadshotMonthlyModal = ({ onClose }: UseHeadshotMonthlyModalParams) => {
  const { headshotEligibility, myAccount, refetchHeadshotEligibility } = useArtistContext();
  const { formatIntlDate } = useTranslation();
  const billingPopup = useBillingPopup();
  const { navigate } = useRouter();
  const nextBookableAt = headshotEligibility?.creditsResetAt ?? headshotEligibility?.nextBookableAt;

  const nextBookableDate = nextBookableAt
    ? formatIntlDate({
        date: nextBookableAt,
        format: 'MONTH_LONG_DAY_YEAR',
        timeZoneStandardName: myAccount?.timeZone?.standardName,
        replaceUtc: false,
      })
    : '';

  const handleMonthlyUpgrade = useCallback(async () => {
    captureHeapEvent({
      name: HEAP_EVENT_NAMES.TALENT_CLICK_HEADSHOTS_MONTHLY_UPGRADE_MODULE_UPGRADE_NOW,
    });
    onClose();

    const { getPurchaseResult } = billingPopup.show({
      location: PaywallLocation.HEADSHOTS_MONTHLY,
    });

    if (!getPurchaseResult) {
      return;
    }

    let purchaseResult: Awaited<ReturnType<typeof getPurchaseResult>>;
    try {
      purchaseResult = await getPurchaseResult();
    } catch (error) {
      if (error === 'Component unmounted') {
        return;
      }

      throw error;
    }

    if (purchaseResult.type === PurchaseResultType.Success) {
      refetchHeadshotEligibility();
      navigate({ to: routeHelper.headshots.link() });
    }
  }, [billingPopup, navigate, onClose, refetchHeadshotEligibility]);

  return useMemo(() => ({ nextBookableDate, handleMonthlyUpgrade }), [handleMonthlyUpgrade, nextBookableDate]);
};

export type UseHeadshotMonthlyModalValues = ReturnType<typeof useHeadshotMonthlyModal>;
